import React from 'react';
import styled from 'styled-components';

import { TOGGLE_ICON_SIZE } from '@front/common/src/components/accordion/constants';
import { getCurrentCountry } from '@front/common/src/utils/country';

import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';

import { translate } from '@front/i18n';

import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import Alert from '@front/web-ui/src/components/alert/alert';

import { useIsITCSelectedOrSuccessfullyPaired } from '../../../../../hooks/use-is-itc-selected-or-successfully-paired';

const StyledAlert = styled(Alert)`
  margin: 10px 0;
`;

const t = translate('basket.cleaning.services');

const StyledSubtitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  margin-right: ${15 + TOGGLE_ICON_SIZE}px;
`;

type PrestationPricesTitlePreviewProps = {
  formattedPrice: string;
  isITCSelectedOrSuccessfullyPaired: boolean;
  isLoadingPrices: boolean;
};

export const prestationPricesTitlePreview = ({
  formattedPrice,
  isITCSelectedOrSuccessfullyPaired,
  isLoadingPrices,
}: PrestationPricesTitlePreviewProps) =>
  isLoadingPrices ? (
    <Skeleton linesWidth={['110px']} />
  ) : (
    <Text strikedOut={isITCSelectedOrSuccessfullyPaired}>{formattedPrice}</Text>
  );

type AccordionSubtitleProps = {
  formattedPrice?: string;
  hasTaxCredit?: boolean;
  isITCSelectedOrSuccessfullyPaired: boolean;
  isLoading: boolean;
};

export const AccordionSubtitle = ({
  formattedPrice,
  hasTaxCredit,
  isITCSelectedOrSuccessfullyPaired,
  isLoading,
}: AccordionSubtitleProps) => {
  const firstLine = isITCSelectedOrSuccessfullyPaired ? (
    <Text.MDBold>{formattedPrice}</Text.MDBold>
  ) : (
    <Text>{`${t('base.or')} ${formattedPrice}`}</Text>
  );

  const secondLine = isITCSelectedOrSuccessfullyPaired ? null : (
    <Text.SM>{`\xa0${t('.accordion_subtitle_after_tax')}`}</Text.SM>
  );

  if (isLoading)
    return (
      <StyledSubtitle>
        <Skeleton linesWidth={['70px', '70px']} />
      </StyledSubtitle>
    );

  if (hasTaxCredit)
    return (
      <StyledSubtitle>
        {firstLine}
        {secondLine}
      </StyledSubtitle>
    );

  return undefined;
};

type SubtitleProps = {
  formattedPriceAfterTax: string;
  hasTaxCredit?: boolean;
  hasTaxCreditInBasket: boolean;
  isITCSelectedOrSuccessfullyPaired: boolean;
  isLoadingPrices: boolean;
};

export const subtitle = ({
  formattedPriceAfterTax,
  hasTaxCredit,
  hasTaxCreditInBasket,
  isITCSelectedOrSuccessfullyPaired,
  isLoadingPrices,
}: SubtitleProps) =>
  hasTaxCreditInBasket ? (
    <AccordionSubtitle
      formattedPrice={formattedPriceAfterTax}
      hasTaxCredit={hasTaxCredit}
      isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
      isLoading={isLoadingPrices}
    />
  ) : undefined;

export const usePrestationPriceAlerts = () => {
  const { shouldShowInstantTaxCreditSelectionScreen } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  const { isWecasaCarePairingSuccessful, isInstantTaxCreditSelected, shouldShowTaxCreditPriceInfo } =
    useIsITCSelectedOrSuccessfullyPaired();

  if (!shouldShowInstantTaxCreditSelectionScreen || !shouldShowTaxCreditPriceInfo) {
    return null;
  }

  if (isWecasaCarePairingSuccessful) {
    return <StyledAlert content={t('.tax.itc_alert.paired_successfully')} type='success' />;
  }
  if (isInstantTaxCreditSelected) {
    return <StyledAlert content={t('.tax.itc_alert.itc_selected')} type='success' />;
  }
  return <StyledAlert content={t('.tax.itc_alert.itc_not_selected')} type='info' />;
};
